/**
 * FDV — bus de analítica del tema.
 * Ver docs/analitica-y-cro/13-cro-analytics-tracking.md (§8).
 *
 * Un único punto que normaliza y reparte los eventos custom `fdv_*` a:
 *   1. Shopify.analytics.publish  → Web Pixels sandbox → FDV CRO Pixel → gateway/warehouse
 *   2. window.__fdvOpenReplay     → OpenReplay (cuando esté cargado)
 *   3. window.dataLayer           → GTM, si algún día entra
 *
 * Los eventos ESTÁNDAR de Horizon (product_viewed, product_added_to_cart,
 * page_viewed, checkout_*) NO pasan por aquí — los emite Shopify solo vía
 * `standard-events.js`. Este bus es solo para los `fdv_*` de negocio/CRO.
 */
(() => {
  const cfg = readConfig();
  const DEBUG = cfg.debug === true || safeLS('get', 'fdv_debug') === '1';

  const FDV = (window.FDV = window.FDV || {});
  if (FDV.track) return; // ya inicializado

  // --- Identidad (unificada con OpenReplay y GrowthBook) ---------------------
  // `fdv_identity` = { email_sha256?, phone_sha256?, uid? } en localStorage.
  // Lo lee el FDV CRO Pixel (pixel/fdv-cro-pixel.js) para enriquecer eventos
  // server-side de visitantes conocidos-anónimos. Ver 13-*.md §3.2 y Etapa D.
  FDV.anonId = readOrCreateAnonId();
  FDV.identity = readIdentity();

  /** Persiste identificadores de primera parte para enriquecer eventos server-side. */
  FDV.identify = (obj) => {
    if (!obj || typeof obj !== 'object') return FDV.identity;
    const next = { ...FDV.identity };
    for (const [k, v] of Object.entries(obj)) {
      if (v != null && v !== '' && next[k] !== v) next[k] = v;
    }
    FDV.identity = next;
    safeLS('set', 'fdv_identity', JSON.stringify(next));
    return next;
  };

  // Cliente con sesión iniciada: el snippet ya calculó el hash del email
  // server-side (`| sha256`), sincrónico y fiable. Sella la identidad para
  // todas las páginas siguientes.
  if (cfg.customer_email_sha256) {
    FDV.identify({ email_sha256: cfg.customer_email_sha256, uid: strOrNull(cfg.customer_id) });
  }

  // Click IDs de anuncios: sólo vienen en la URL de aterrizaje. Persistir
  // `_fbc` como cookie (igual que haría el pixel de Meta) para que el FDV CRO
  // Pixel lo lea en los eventos posteriores, no sólo en la landing.
  captureClickIds();

  // --- Cliente "reclamado" desde un enlace de correo (Mailchimp) ------------
  // Los correos de Mailchimp traen `?c_id=<shopify customer id>` (o `?fdv_c=`).
  // Es una PISTA NO VERIFICADA: el id de cliente de Shopify es numérico y
  // enumerable, cualquiera puede falsificarlo. Por eso NO sella `FDV.identity`
  // ni dispara CAPI — sólo viaja como `claimed_customer_id` en el payload de
  // los eventos `fdv_*` para que el gateway lo VALIDE server-side (resolución
  // visitor→customer contra Shopify, ver 14-*.md §"resolución de identidad").
  // Uso client-side seguro: suprimir el popup de captura de correo a quien ya
  // es cliente/suscriptor (peor caso si falla: un visitante nuevo no ve un popup).
  FDV.claimedCustomerId = readOrCaptureClaimedCid();

  // --- Consentimiento ------------------------------------------------------
  // Default permisivo (= comportamiento actual del sitio en Impulse); se
  // ajusta con la Customer Privacy API de Shopify cuando el banner esté puesto.
  FDV.consent = { analytics: true, marketing: true };
  initConsent();

  // --- Núcleo ------------------------------------------------------------
  const dataLayer = (window.dataLayer = window.dataLayer || []);

  /**
   * @param {string} name  nombre del evento, con prefijo `fdv_`
   * @param {object} [detail]  campos propios del evento
   * @returns {string|undefined}  el event_id (para dedup browser↔server)
   */
  FDV.track = (name, detail = {}) => {
    if (typeof name !== 'string' || !name) return;

    const evt = {
      event_id: uuid(),
      name,
      ts: Date.now(),
      anon_id: FDV.anonId,
      customer_id: cfg.customer_id ?? null,
      // Pista sin verificar de un enlace de correo — el gateway la valida. Se
      // omite si ya hay login real (el `customer_id` de arriba manda).
      claimed_customer_id: cfg.customer_id ? null : FDV.claimedCustomerId ?? null,
      template: cfg.template ?? null,
      page_type: cfg.page_type ?? null,
      url: location.href,
      referrer: document.referrer || null,
      ...detail,
    };

    if (FDV.consent.analytics) {
      try {
        window.Shopify?.analytics?.publish?.(name, evt);
      } catch (e) {
        warn('Shopify.analytics.publish', e);
      }
    }
    try {
      window.__fdvOpenReplay?.event?.(name, evt);
    } catch (e) {
      /* OpenReplay aún no cargado o sin consent — no pasa nada */
    }
    try {
      dataLayer.push({ event: name, ...evt });
    } catch (e) {
      /* noop */
    }

    if (DEBUG) console.debug('[FDV]', name, evt);
    return evt.event_id;
  };

  FDV.ready = Promise.resolve(FDV);

  // Helpers compartidos: `fdv-campaign.js` los usa para sellar identidad desde
  // el email capturado (nunca manda el email en claro al bus).
  FDV.normEmail = normEmail;
  FDV.sha256 = sha256Hex;

  // --- Listeners: derivar `fdv_*` de los Standard Events de Horizon ---------
  wireSearchAndFilterEvents();
  wireNewsletterSignup();
  wireHeroEvents();
  wireReviewSummaryClick();
  wireBeginCheckout();
  wireLoyaltyCtaClick();
  wireNewsletterOutcome();
  wireCustomerLogin();
  // Nota: `fdv_discount_code_applied` / `_rejected` NO se derivan acá — los emite
  // directo `assets/cart-discount.js` (patch #33), que conoce el resultado exacto
  // de cada rama (aplicado / inválido / no-aplica-a-envío).

  // Nota: `cart_drawer_viewed` de Impulse ya no hace falta — Horizon emite
  // `shopify:cart:view` con `context: 'dialog'` de forma nativa.

  // =======================================================================
  // Helpers
  // =======================================================================

  function readConfig() {
    try {
      return JSON.parse(document.getElementById('fdv-analytics-config')?.textContent || '{}');
    } catch (e) {
      return {};
    }
  }

  function uuid() {
    try {
      if (crypto?.randomUUID) return crypto.randomUUID();
    } catch (e) {
      /* fallthrough */
    }
    // RFC4122 v4 aproximado — suficiente para dedup de eventos
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
  }

  function safeLS(op, key, value) {
    try {
      if (op === 'get') return localStorage.getItem(key);
      if (op === 'set') return localStorage.setItem(key, value);
    } catch (e) {
      return null;
    }
  }

  function readOrCreateAnonId() {
    let id = safeLS('get', 'fdv_anon_id');
    if (!id) {
      id = uuid();
      safeLS('set', 'fdv_anon_id', id);
    }
    return id;
  }

  function readIdentity() {
    try {
      const obj = JSON.parse(safeLS('get', 'fdv_identity') || '{}');
      return obj && typeof obj === 'object' ? obj : {};
    } catch (e) {
      return {};
    }
  }

  function strOrNull(v) {
    return v == null || v === '' ? null : String(v);
  }

  function normEmail(v) {
    return typeof v === 'string' && v.trim() ? v.trim().toLowerCase() : null;
  }

  /** SHA-256 hex — misma normalización que el server (email en minúsculas + trim). */
  async function sha256Hex(s) {
    try {
      const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(s));
      return [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, '0')).join('');
    } catch (e) {
      return null;
    }
  }

  /**
   * Persiste `_fbc` (Meta) desde `fbclid` de la URL de aterrizaje. `_ttp` (TikTok)
   * y `_fbp` los pone el pixel de cada plataforma; el FDV CRO Pixel lee las tres
   * cookies. `gclid` va en la URL y el pixel lo toma de ahí.
   */
  function captureClickIds() {
    try {
      const params = new URLSearchParams(location.search);
      const fbclid = params.get('fbclid');
      if (fbclid && !/(^|;\s*)_fbc=/.test(document.cookie)) {
        const val = `fb.1.${Date.now()}.${fbclid}`;
        document.cookie = `_fbc=${val}; path=/; max-age=7776000; samesite=lax`;
      }
    } catch (e) {
      /* noop */
    }
  }

  /**
   * Lee `?c_id=` / `?fdv_c=` de la URL de aterrizaje y lo persiste en
   * `localStorage.fdv_claimed_cid` para las páginas siguientes. Devuelve el id
   * (string de dígitos) o `null`. NUNCA es identidad confiable — ver el bloque
   * de "cliente reclamado" arriba.
   */
  function readOrCaptureClaimedCid() {
    try {
      const params = new URLSearchParams(location.search);
      const raw = params.get('fdv_c') || params.get('c_id');
      const fromUrl = raw && /^\d{1,15}$/.test(raw.trim()) ? raw.trim() : null;
      if (fromUrl) {
        safeLS('set', 'fdv_claimed_cid', fromUrl);
        return fromUrl;
      }
      const stored = safeLS('get', 'fdv_claimed_cid');
      return stored && /^\d{1,15}$/.test(stored) ? stored : null;
    } catch (e) {
      return null;
    }
  }

  function initConsent() {
    const apply = () => {
      try {
        const cp = window.Shopify?.customerPrivacy;
        if (!cp) return;
        FDV.consent = {
          analytics: cp.analyticsProcessingAllowed?.() ?? true,
          marketing: cp.marketingAllowed?.() ?? true,
        };
        document.dispatchEvent(new CustomEvent('fdv:consent', { detail: FDV.consent }));
        if (DEBUG) console.debug('[FDV] consent', FDV.consent);
      } catch (e) {
        /* API no disponible — se queda permisivo */
      }
    };

    try {
      window.Shopify?.loadFeatures?.(
        [{ name: 'consent-tracking-api', version: '0.1' }],
        (error) => {
          if (error) return;
          apply();
          document.addEventListener('visitorConsentCollected', apply);
        }
      );
    } catch (e) {
      /* noop */
    }
  }

  function debounce(fn, ms) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), ms);
    };
  }

  function compactFilters(list) {
    if (!Array.isArray(list)) return [];
    return list.map((f) => {
      const [key] = Object.keys(f || {});
      return key ? { [key]: f[key] } : f;
    });
  }

  function wireSearchAndFilterEvents() {
    // Búsqueda sin resultados + filtro aplicado en /search
    document.addEventListener('shopify:search:update', (e) => {
      const query = e?.search?.query ?? '';
      const filters = compactFilters(e?.search?.productFilters);
      if (filters.length) {
        emitFilterApplied({ context: 'search', query, filters, sort: e?.search?.sortKey ?? null });
      }
      Promise.resolve(e?.promise)
        .then((res) => {
          if (res && res.totalCount === 0) FDV.track('fdv_search_no_results', { query, filters });
        })
        .catch(() => {});
    });

    // Filtro / orden aplicado en una colección
    document.addEventListener('shopify:collection:update', (e) => {
      emitFilterApplied({
        context: 'collection',
        collection: e?.collection?.handle ?? cfg.template ?? null,
        filters: compactFilters(e?.productFilters),
        sort: e?.sortKey ?? null,
      });
    });
  }

  const emitFilterApplied = debounce((payload) => {
    if (!payload.filters?.length && !payload.sort) return;
    FDV.track('fdv_filter_applied', payload);
  }, 400);

  // Hash del email tecleado en un form de newsletter, pre-calculado de forma
  // asíncrona (`crypto.subtle` es async y el submit navega). Se refresca en cada
  // `input` válido; en el submit ya está listo para usarse sincrónicamente.
  let pendingEmailHash = null;
  const primeEmailHash = debounce((raw) => {
    const email = normEmail(raw);
    if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) return;
    sha256Hex(email).then((hash) => {
      if (!hash) return;
      pendingEmailHash = hash;
      FDV.identify({ email_sha256: hash });
    });
  }, 300);

  function isSignupForm(form) {
    if (!(form instanceof HTMLFormElement)) return false;
    return (
      form.classList.contains('email-signup__form') ||
      (/\/contact/.test(form.getAttribute('action') || '') &&
        !!form.querySelector('input[name="contact[email]"]'))
    );
  }

  function wireNewsletterSignup() {
    // El bloque `email-signup` de Horizon es un {% form 'customer' %} → POST de
    // página completa. Disparamos sincrónicamente en el submit (Shopify.analytics
    // y dataLayer son sync y sobreviven la navegación).
    document.addEventListener(
      'input',
      (e) => {
        const el = e.target;
        if (!(el instanceof HTMLInputElement)) return;
        if (el.type !== 'email' && el.name !== 'contact[email]') return;
        if (!isSignupForm(el.form)) return;
        primeEmailHash(el.value);
      },
      true
    );

    document.addEventListener(
      'submit',
      (e) => {
        const form = e.target;
        if (!isSignupForm(form)) return;

        const emailInput = form.querySelector('input[name="contact[email]"], input[type="email"]');
        const emailHash = pendingEmailHash;
        // Fallback: si el usuario pegó el email y envió sin disparar `input`, el
        // hash no llega a tiempo para ESTE evento, pero `FDV.identify` lo persiste
        // en localStorage → el pixel lo toma en las páginas siguientes.
        if (!emailHash && emailInput) {
          const email = normEmail(emailInput.value);
          if (email) sha256Hex(email).then((h) => h && FDV.identify({ email_sha256: h }));
        }

        const sectionId =
          form.closest('[id^="shopify-section-"]')?.id?.replace('shopify-section-', '') || null;

        const detail = {
          source: sectionId && /footer/i.test(sectionId) ? 'footer' : sectionId || 'unknown',
        };
        // Sólo el hash — nunca el email en claro (iría a dataLayer / OpenReplay).
        if (emailHash) detail.email_sha256 = emailHash;

        FDV.track('fdv_newsletter_signup', detail);
      },
      true
    );
  }

  // El slideshow de la home = el "hero". `slideshow:select` (de `assets/events.js`,
  // `bubbles: true`) trae { index, id, slide, previousIndex, userInitiated, trigger }.
  // Se emite `fdv_hero_slide_view` una vez por slide y página (dedup por índice) +
  // siempre que el visitante navega a mano; el autoplay no genera ruido.
  function wireHeroEvents() {
    if (cfg.page_type !== 'index') return;

    const seenSlides = new Set();
    const emitSlideView = (index, slide, trigger, userInitiated) => {
      if (index == null) return;
      const first = !seenSlides.has(index);
      seenSlides.add(index);
      if (!first && !userInitiated) return;
      FDV.track('fdv_hero_slide_view', {
        index,
        slide_id: (slide && slide.getAttribute('slide-id')) || null,
        heading: slideHeading(slide),
        trigger: trigger ?? null,
        user_initiated: !!userInitiated,
      });
    };

    document.addEventListener('slideshow:select', (e) => {
      const d = e?.detail;
      if (!d || !isHomeSlideshow(e.target)) return;
      emitSlideView(d.index, d.slide, d.trigger, d.userInitiated);
    });

    // El slideshow no emite `slideshow:select` para el slide inicial (índice 0):
    // sale del `select()` sin disparar. Emitimos su vista a mano una vez montado.
    const seedInitial = () => {
      const sc = document.querySelector('slideshow-component');
      const slides = sc ? [...sc.querySelectorAll('slideshow-slide')] : [];
      if (!slides.length) return false;
      const active = slides.find((s) => s.getAttribute('aria-hidden') !== 'true') || slides[0];
      emitSlideView(slides.indexOf(active), active, 'initial', false);
      return true;
    };
    if (!seedInitial()) {
      let tries = 0;
      const iv = setInterval(() => {
        if (seedInitial() || ++tries > 20) clearInterval(iv);
      }, 250);
    }

    // Click en el botón (CTA) de un slide — el bloque `button` de Horizon renderiza
    // un <a class="button">. Los controles del slideshow (flechas / puntos) viven
    // fuera de <slideshow-slide>, así que el scope los excluye solo.
    document.addEventListener('click', (e) => {
      const link = e.target instanceof Element ? e.target.closest('a, button') : null;
      if (!link) return;
      const slide = link.closest('slideshow-slide');
      if (!slide || !isHomeSlideshow(slide)) return;
      const slides = [...(slide.parentElement?.children || [])].filter((n) => n.tagName === 'SLIDESHOW-SLIDE');
      FDV.track('fdv_hero_cta_click', {
        index: slides.indexOf(slide),
        slide_id: slide.getAttribute('slide-id') || null,
        cta_text: (link.textContent || '').trim().slice(0, 120) || null,
        href: link.getAttribute('href') || null,
      });
    });
  }

  function isHomeSlideshow(node) {
    const sc = node instanceof Element ? node.closest('slideshow-component') : null;
    if (!sc) return false;
    // El primer slideshow del template `index` = el hero. (Evita disparar en
    // galerías de producto u otros slideshows que compartan el custom element.)
    return document.querySelector('slideshow-component') === sc;
  }

  function slideHeading(slide) {
    if (!(slide instanceof Element)) return null;
    const h = slide.querySelector('h1, h2, h3, [class*="heading"]');
    const txt = h && h.textContent ? h.textContent.trim() : '';
    return txt ? txt.slice(0, 160) : null;
  }

  // Click en "Finalizar compra" (`#checkout` / `[name="checkout"]`, en `/cart` y en
  // el cart drawer). El `<form>` del carrito navega en la fase de burbuja → emitimos
  // en captura y sincrónicamente (`Shopify.analytics`, `dataLayer` y el buffer de
  // OpenReplay sobreviven la navegación). Es la única marca de intención de checkout
  // que puede entrar a la grabación de OpenReplay: el checkout en sí es otro origen y
  // sus eventos (`checkout_started` … `purchase`) los ve solo el pixel. El gateway
  // enlaza ambos vía el atributo `_or_session` que pone `fdv-openreplay.js`.
  // Ver docs/analitica-y-cro/23-openreplay-checkout-identidad.md y 13-*.md §9.
  function wireBeginCheckout() {
    document.addEventListener(
      'click',
      (e) => {
        const btn =
          e.target instanceof Element
            ? e.target.closest('#checkout, button[name="checkout"], [name="checkout"]')
            : null;
        if (!btn) return;
        const inDrawer = !!btn.closest('#cart-drawer, cart-drawer-component');
        const cart = window.FDV?.gifts?.cart || null;
        const offers = window.FDV?.gifts?.state;
        FDV.track('fdv_begin_checkout', {
          surface: inDrawer ? 'cart_drawer' : 'cart_page',
          cart_total: cart ? (cart.total_price ?? cart.items_subtotal_price ?? null) : null, // centavos
          currency: cart?.currency || cfg.currency || 'MXN',
          item_count: cart ? (cart.item_count ?? null) : null,
          has_gift: Array.isArray(offers) && offers.some((s) => s && s.status === 'unlocked'),
        });
      },
      true
    );
  }

  // Accesos al Club de Hechicería (patch #47): «Mis hechizos» del encabezado y la
  // línea del Club en la ficha y el carrito (`snippets/fdv-club-{entry,note}.liquid`).
  // Un solo evento con `source` (header | header_drawer | product | cart). Es un
  // clic, no un login: el login se mide aparte (`fdv_customer_login`).
  function wireLoyaltyCtaClick() {
    document.addEventListener(
      'click',
      (e) => {
        const el = e.target instanceof Element ? e.target.closest('[data-fdv-loyalty-cta]') : null;
        if (!el) return;
        const href = el.getAttribute('href') || '';
        FDV.track('fdv_loyalty_cta_click', {
          source: el.getAttribute('data-fdv-loyalty-cta') || 'unknown',
          logged_in: !!cfg.customer_id,
          // La cuenta vive en `/account` o en el dominio de cuentas (account.flordevenus.com.mx).
          destination: /\/\/account\.|\/account(\/|$|\?)/.test(href) ? 'account' : 'club_page',
        });
      },
      true
    );
  }

  // Resultado del newsletter. `fdv_newsletter_signup` es el INTENTO (submit); aquí
  // se mide lo que Shopify respondió al recargar la página: el bloque `email-signup`
  // pinta `#Email-signup__message-success-*` o `#Email-signup__message-error-*`.
  // Se quita `customer_posted` de la URL para que recargar no lo cuente otra vez.
  // Éxito = Shopify aceptó el correo; no distingue un correo nuevo de uno ya suscrito.
  function wireNewsletterOutcome() {
    const ok = document.querySelector('[id^="Email-signup__message-success-"]');
    const err = document.querySelector('[id^="Email-signup__message-error-"]');
    if (!ok && !err) return;
    const el = ok || err;
    const sectionId = el.closest('[id^="shopify-section-"]')?.id?.replace('shopify-section-', '') || null;
    FDV.track(ok ? 'fdv_newsletter_success' : 'fdv_newsletter_error', {
      source: sectionId && /footer/i.test(sectionId) ? 'footer' : sectionId || 'unknown',
    });
    try {
      const url = new URL(location.href);
      if (url.searchParams.has('customer_posted')) {
        url.searchParams.delete('customer_posted');
        history.replaceState(history.state, '', url.pathname + url.search + url.hash);
      }
    } catch (e) {
      /* noop */
    }
  }

  // Inicio de sesión observado en la tienda: la página anterior en este navegador
  // era anónima y esta trae `customer_id`. Solo guarda 0/1 (nada personal). No se
  // cuenta la primera visita sin estado previo, para no confundir una sesión que ya
  // existía con un login nuevo.
  function wireCustomerLogin() {
    const now = cfg.customer_id ? '1' : '0';
    const prev = safeLS('get', 'fdv_auth_state');
    if (prev === '0' && now === '1') FDV.track('fdv_customer_login', {});
    if (prev !== now) safeLS('set', 'fdv_auth_state', now);
  }

  // Click en el resumen de estrellas del PDP (app block `rating-summary` de
  // flor-de-venus-reviews → `<a class="fdv-rating-summary" data-fdv-rating-summary
  // data-product-id>`). Señal de interés en prueba social. Ver 13-*.md §9.
  function wireReviewSummaryClick() {
    document.addEventListener('click', (e) => {
      const el = e.target instanceof Element ? e.target.closest('[data-fdv-rating-summary]') : null;
      if (!el) return;
      FDV.track('fdv_review_summary_click', {
        product_id: el.getAttribute('data-product-id') || strOrNull(cfg.product_id),
      });
    });
  }

  function warn(where, err) {
    if (DEBUG) console.warn('[FDV]', where, err);
  }
})();
